import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { billingApi, cckCreditsApi } from '../services/api';
import { SubscriptionInfo, CckCreditSummary } from '../types';
import { showsUsage, usagePercent } from '../services/billingUtil';
import { CckCreditsPanel, CckCreditLogs } from '../components/CckCredits';

const TIER_LABEL: Record<string, string> = { starter: 'Starter', growth: 'Growth', pro: 'Pro' };

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const Subscription: React.FC = () => {
  const [params] = useSearchParams();
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sm, setSm] = useState<CckCreditSummary | null>(null);

  const justSubscribed = params.get('status') === 'success';
  // Stripe redirect → land on Subscription tab; otherwise default to Credits (the
  // active free-credit feature) so clicking "Billing" shows credit details first.
  const initialTab = (justSubscribed || params.get('tab') === 'subscription') ? 'subscription' : 'credits';
  const [tab, setTab] = useState<'credits' | 'subscription'>(initialTab);

  const load = () => {
    setLoading(true);
    cckCreditsApi.getSummary().then(setSm).catch(() => {});  // combined total (CCK + plan)
    billingApi.getSubscription()
      .then(setSub)
      .catch(() => setError('Could not load your subscription.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const manage = async () => {
    setError(null);
    setBusy(true);
    try {
      const url = await billingApi.openPortal();
      window.location.assign(url);
    } catch {
      setError('We couldn’t open the billing portal. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, marginBottom: 4, color: 'var(--text-strong)' }}>Billing</h2>
      <p style={{ marginBottom: 16, color: 'var(--text-soft)' }}>Your free credits and subscription plan.</p>

      {/* Combined total — the single number that reflects BOTH trial credits and plan allotment. */}
      {sm && sm.metered && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
          padding: '12px 16px', marginBottom: 16, borderRadius: 8,
          border: '1px solid var(--amber)',
          background: 'linear-gradient(135deg, rgba(245,166,35,0.14), rgba(0,180,160,0.08))',
        }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--amber)', lineHeight: 1 }}>
            {sm.total_remaining.toLocaleString()}
          </span>
          <span style={{ color: 'var(--text-soft)', fontSize: 13 }}>credits available</span>
          <span style={{ marginLeft: 'auto', color: 'var(--text-faint)', fontSize: 12 }}>
            {(sm.cck.enabled ? sm.cck.balance : 0).toLocaleString()} trial · {(sm.stripe?.spendable ? (sm.stripe.remaining ?? 0) : 0).toLocaleString()} plan
          </span>
        </div>
      )}

      {/* ── Tabs: Credit Management (CCK) · Subscription (Stripe) — two separate areas (D-8) ── */}
      <div style={tabBar}>
        <button onClick={() => setTab('credits')} style={tabBtn(tab === 'credits')}>Credit Management</button>
        <button onClick={() => setTab('subscription')} style={tabBtn(tab === 'subscription')}>Subscription</button>
      </div>

      {tab === 'credits' && (
        <div>
          <CckCreditsPanel />
          <CckCreditLogs />
        </div>
      )}

      {tab === 'subscription' && (
        <div>
      {justSubscribed && (
        <div className="alert alert-success" style={{ marginBottom: 14 }}>
          ✅ You’re subscribed — your dial credits are ready.
        </div>
      )}
      {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>❌ {error}</div>}

      {loading ? (
        <div style={{ color: 'var(--text-faint)' }}>Loading…</div>
      ) : !sub ? null : !sub.enforced ? (
        <div style={card}>
          <div style={tierName}>Unlimited</div>
          <p style={{ color: 'var(--text-soft)', marginTop: 8, fontSize: 13 }}>
            Your account isn’t metered — dial without credit limits.
          </p>
        </div>
      ) : sub.tier == null ? (
        <div style={card}>
          <div style={tierName}>No active plan</div>
          <p style={{ color: 'var(--text-soft)', margin: '8px 0 14px', fontSize: 13 }}>
            Choose a plan to start verifying numbers.
          </p>
          <Link to="/pricing" className="sidebar-new-run-btn" style={{ textDecoration: 'none', display: 'inline-flex' }}>
            View plans
          </Link>
        </div>
      ) : (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <div style={tierName}>{TIER_LABEL[sub.tier] ?? sub.tier}</div>
              <div style={statusLine}>
                {sub.status}
                {sub.cancel_at_period_end && sub.current_period_end
                  ? ` · cancels ${formatDate(sub.current_period_end)}`
                  : sub.current_period_end ? ` · renews ${formatDate(sub.current_period_end)}` : ''}
              </div>
            </div>
            <button onClick={manage} disabled={busy} className="sidebar-new-run-btn">
              {busy ? 'Opening…' : 'Manage Billing'}
            </button>
          </div>

          {showsUsage(sub) && (
            <div style={{ marginTop: 18 }}>
              <div style={usageRow}>
                <span>{(sub.used ?? 0).toLocaleString()} / {(sub.included_dials ?? 0).toLocaleString()} dials used</span>
                <span style={{ color: 'var(--amber)' }}>{(sub.remaining ?? 0).toLocaleString()} left</span>
              </div>
              <div style={barTrack}>
                <div style={{ ...barFill, width: `${usagePercent(sub.used, sub.included_dials)}%` }} />
              </div>
            </div>
          )}
        </div>
      )}
        </div>
      )}
    </div>
  );
};

const tabBar: React.CSSProperties = {
  display: 'flex', gap: 4, borderBottom: '2px solid var(--border)', marginBottom: 18,
};
const tabBtn = (active: boolean): React.CSSProperties => ({
  background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px',
  fontFamily: 'var(--font-data)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em',
  color: active ? 'var(--amber)' : 'var(--text-faint)',
  borderBottom: active ? '2px solid var(--amber)' : '2px solid transparent', marginBottom: -2,
});

const card: React.CSSProperties = {
  background: 'var(--surface)', border: '2px solid var(--border)',
  boxShadow: '6px 6px 0 rgba(0,0,0,0.5)', padding: '20px 22px', maxWidth: 560,
};
const tierName: React.CSSProperties = {
  color: 'var(--text-strong)', fontFamily: 'var(--font-display)', fontSize: 20, letterSpacing: '0.04em',
};
const statusLine: React.CSSProperties = {
  marginTop: 4, color: 'var(--text-faint)', fontFamily: 'var(--font-data)', fontSize: 12,
  letterSpacing: '0.06em', textTransform: 'uppercase',
};
const usageRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-data)', fontSize: 12,
  color: 'var(--text-soft)', marginBottom: 6, letterSpacing: '0.04em',
};
const barTrack: React.CSSProperties = {
  height: 10, background: 'var(--surface-subtle)', border: '1px solid var(--border)', overflow: 'hidden',
};
const barFill: React.CSSProperties = { height: '100%', background: 'var(--amber)' };

export default Subscription;
